import React from 'react';

const ScriptsPage = () => {
  const getTagClasses = (tag) => {
    switch (tag.toLowerCase()) {
      case 'esx':
        return 'bg-[#ef9d38] text-gray-900'; // ESX: #ef9d38 (yellow/orange)
      case 'qb':
      case 'qbcore':
        return 'bg-[#ca3142] text-white'; // QB/QBCore: #ca3142 (pink/red)
      case 'qbx':
        return 'bg-[#5DB6E5] text-white'; // QBX: Using HUD_COLOUR_BLUE #5DB6E5 [1]
      case 'standalone':
        return 'bg-teal-600 text-white'; // Teal for Standalone
      case 'ox_inventory':
      case 'qb-inventory':
      case 'qs_inventory':
        return 'bg-gray-700 text-gray-200'; // Dark grey for inventory systems
      default:
        return 'bg-devcore.dark bg-opacity-50 text-gray-300'; // Default style
    }
  };

  const scriptsData = [
    {
      name: "Banking System",
      description: "A highly configurable banking solution offering multi-language UI, flexible banking access methods (ox_target, okok_textui, ox_lib, ESX, CD), and comprehensive transaction logging via Discord webhooks.",
      compatibility: ["ESX"],
      icon: "fas fa-bank",
      url: "https://store.devcore.cz/package/6892700" // No direct product URL provided, using main store
    },
    {
      name: "Realistic Smoking",
      description: "An ultra-optimized (0.01ms) and highly customizable smoking script featuring synchronized effects, 8 unique animations, a rolling minigame, and a dynamic 'high' effect system.",
      compatibility: ["ESX", "QB", "QBX"],
      icon: "fas fa-smoking",
      url: "https://store.devcore.cz/package/4486757" // No direct product URL provided, using main store
    },
    {
      name: "Realistic Needs",
      description: "A feature-rich needs system with 0 ms inactive optimization, including a comprehensive drunk system with movement changes and breathalyzer, customizable menus, and dynamic item consumption/placement.",
      compatibility: ["ESX", "QB", "QBX"],
      icon: "fas fa-utensils",
      url: "https://store.devcore.cz/package/4623206" // No direct product URL provided, using main store
    },
    {
      name: "Multijob",
      description: "A versatile job management script compatible with ESX, offering full configurability for up to 3 simultaneous jobs with automatic rotation. Features include admin commands, a modern responsive UI, optimized performance, and support for multiple notification systems.",
      compatibility: ["ESX", "QB", "QBX"],
      icon: "fas fa-briefcase",
      url: "https://store.devcore.cz/package/6835179" // No direct product URL provided, using main store
    },
    {
      name: "Water Gun",
      description: "An escrow-protected water gun script with configurable buttons for splash, reload, and pump actions. It allows for customizable water gun items with adjustable tank capacity and pressure ranges, ensuring a fun and interactive experience.",
      compatibility: ["ESX", "QB", "QBX"],
      icon: "fas fa-water",
      url: "https://store.devcore.cz/package/4498224"
    },
    {
      name: "Drug Sales",
      description: "An advanced NPC drug sales script with extensive configuration, allowing players to sell drugs in vehicles and negotiate prices. Features include dynamic police alerts, buyer interaction options, gang member interactions, and optimized performance (0.1 ms).",
      compatibility: ["ESX"],
      icon: "fas fa-flask",
      url: "https://store.devcore.cz/package/4509432" // No direct product URL provided, using main store
    },
    {
      name: "Mileage Meter",
      description: "A realistic mileage meter script for vehicles, featuring automatic display, a dynamic vehicle wear system that affects performance, and a vehicle dirtying system. Includes a mini-game for mileage reduction using special USB items.",
      compatibility: ["ESX"],
      icon: "fas fa-gauge-high",
      url: "https://store.devcore.cz/package/4813342" // No direct product URL provided, using main store
    },
    {
      name: "Tags System",
      description: "A versatile tag system for FiveM servers, offering synchronized 3D UI display, broad framework compatibility, and optimized performance, with flexible permission management for player or group identification.",
      compatibility: ["ESX", "QB", "QBX", "STANDALONE"],
      icon: "fas fa-tags",
      url: "https://store.devcore.cz/package/6914175"
    },
    {
      name: "Key Interactions",
      description: "A robust script for dynamic UI prompts, providing clean, animated key interactions with support for multiple, customizable prompts. Features real-time updates, full color control, and broad compatibility for enhancing in-game guidance and menus.",
      compatibility: ['ESX', 'QB', 'QBX', 'STANDALONE'],
      icon: "fas fa-keyboard",
      url: "https://store.devcore.cz/package/6932072"
    }
  ];

  return (
    <section id="scripts" className="py-20 px-6 gradient-bg pt-32"> {/* Added pt-32 for header offset */}
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our <span className="gradient-text">Script Collection</span></h2>
          <p className="text-gray-300 max-w-2xl mx-auto">Premium quality scripts with unparalleled performance and unique features</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {scriptsData.map((script, index) => (
            <div key={index} className="glass-card p-6 rounded-xl hover:shadow-lg transition">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-lg bg-devcore.primary bg-opacity-20 flex items-center justify-center text-devcore.primary">
                  <i className={`${script.icon} text-2xl feature-icon`}></i>
                </div>
                <h3 className="text-xl font-semibold ml-4">{script.name}</h3>
              </div>
              <p className="text-gray-300 mb-4">{script.description}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {script.compatibility.map((tag, tagIndex) => (
                  <span key={tagIndex} className={`px-3 py-1 rounded-full text-xs ${getTagClasses(tag)}`}>
                    {tag}
                  </span>
                ))}
              </div>
              <a href={script.url} target="_blank" rel="noopener noreferrer" className="text-devcore.primary hover:text-blue-400 flex items-center transition">
                <span>Learn more</span>
                <i className="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-devcore.primary rounded-md text-white hover:bg-blue-600 transition">
            <span>Visit Our Store</span>
            <i className="fas fa-external-link-alt ml-2"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ScriptsPage;